import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { collection, db, doc, getDoc, getDocs } from "../boardData";

// OpenWeatherMap key (client-side, already used by the legacy Sidebar).
export const OWM_API_KEY = "50229c69ed23500083e0004c56b83be4";

// Consistent TV-friendly widget chrome. Widgets render their body inside this.
export function WidgetShell({ title, accent, children, padded = true, className = "" }) {
  return (
    <div className={`sb-card ${className}`}>
      {title ? (
        <div className="sb-card-title" style={accent ? { color: accent } : undefined}>
          {title}
        </div>
      ) : null}
      <div className={`sb-card-body${padded ? " padded" : ""}`}>{children}</div>
    </div>
  );
}

export function EmptyState({ icon = "•", label }) {
  return (
    <div className="sb-empty">
      <div className="sb-empty-icon">{icon}</div>
      <div>{label}</div>
    </div>
  );
}

// A latitude / longitude as a number, or null. Settings docs hold them as
// numbers or as strings ("40.8123"), so both are accepted.
export function toCoord(value, limit) {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : parseFloat(String(value).trim());
  return Number.isFinite(n) && Math.abs(n) <= limit ? n : null;
}

// The first usable lat/lng pair among candidates, or null. (0, 0) is an unset
// placeholder, not a station in the Gulf of Guinea.
function coordPair(lats, lngs) {
  const lat = lats.map((v) => toCoord(v, 90)).find((n) => n !== null) ?? null;
  const lng = lngs.map((v) => toCoord(v, 180)).find((n) => n !== null) ?? null;
  if (lat === null || lng === null || (lat === 0 && lng === 0)) return null;
  return { lat, lng };
}

function coordsOf(d) {
  if (!d) return null;
  return coordPair(
    [d.lat, d.latitude, d.coordinates?.latitude, d.location?.latitude, d.location?.lat],
    [d.long, d.lng, d.longitude, d.coordinates?.longitude, d.location?.longitude, d.location?.lng]
  );
}

// A list that fits its box. When its rows (children with class
// "sb-fit-item") overflow, it scales them down together through the CSS
// variable --s (to no less than `min`, so text stays readable across a
// room); if they still don't fit, it hides rows from the end rather than
// cutting one in half, and reports how many through onHiddenChange (the
// storm panels show "+N more" in their title bar). `itemsKey` changes
// whenever the rows do.
export function FitList({ className = "", itemsKey, min = 0.8, onHiddenChange, children }) {
  const ref = useRef(null);
  const hiddenRef = useRef(-1); // -1: always report the first fit
  const onHidden = useRef(onHiddenChange);
  onHidden.current = onHiddenChange;

  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const items = Array.from(el.children).filter((c) => c.classList.contains("sb-fit-item"));
    items.forEach((c) => {
      c.style.display = "";
    });
    el.style.setProperty("--s", "1");
    const avail = el.clientHeight;
    if (!avail) return;
    const over = () => el.scrollHeight > avail + 1;
    let s = 1;
    if (over()) {
      s = Math.max(min, avail / el.scrollHeight);
      el.style.setProperty("--s", s.toFixed(3));
      while (over() && s > min) {
        s = Math.max(min, s * 0.95);
        el.style.setProperty("--s", s.toFixed(3));
      }
    }
    let hidden = 0;
    for (let i = items.length - 1; i > 0 && over(); i -= 1) {
      items[i].style.display = "none";
      hidden += 1;
    }
    if (hidden !== hiddenRef.current) {
      hiddenRef.current = hidden;
      if (onHidden.current) onHidden.current(hidden);
    }
  }, [min]);

  useLayoutEffect(fit, [fit, itemsKey]);
  // Web fonts can arrive after the first fit and change every row's height.
  useEffect(() => {
    let live = true;
    if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => live && fit());
    }
    return () => {
      live = false;
    };
  }, [fit]);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => fit());
    ro.observe(el);
    return () => ro.disconnect();
  }, [fit]);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}

// A card title with an optional tag: `label` (e.g. a rotating list's
// "1–2 of 5") or else "+N more" for rows that didn't fit.
export function TitleWithMore({ title, more, label }) {
  return (
    <>
      {title}
      {label ? <span className="sb-card-more">{label}</span> : more > 0 && <span className="sb-card-more">+{more} more</span>}
    </>
  );
}

// Resolve agency branding (logo / display name / coordinates) from the various
// places agencies store it, newest path first. Coordinates fall back to the
// Legacy Single Board's (geocoded from its ZIP code). Returns {} until resolved.
export function useAgencyInfo(agency) {
  const [info, setInfo] = useState({});
  useEffect(() => {
    let cancelled = false;
    if (!agency) return undefined;
    (async () => {
      const sources = [
        doc(db, "Agencies", agency, "Settings", "Info"),
        doc(db, `${agency} Settings`, "Info"),
        doc(db, "CRM", agency),
      ];
      const merged = {};
      for (const ref of sources) {
        try {
          const snap = await getDoc(ref);
          if (snap.exists()) {
            const d = snap.data();
            merged.name = merged.name || d.name || d.agencyDisplayName;
            merged.logo = merged.logo || d.logo || d.agencyLogo;
            merged.description = merged.description || d.description || d.agencyDescription;
            const c = merged.lat == null ? coordsOf(d) : null;
            if (c) Object.assign(merged, c);
          }
        } catch (err) {
          /* keep trying other sources */
        }
      }
      if (merged.lat == null) {
        try {
          const snap = await getDocs(collection(db, `${agency} Station Board`));
          const c = snap.docs.map((x) => coordsOf(x.data())).find(Boolean);
          if (c) Object.assign(merged, c);
        } catch (err) {
          /* no legacy board to borrow a location from */
        }
      }
      if (!cancelled) setInfo(merged);
    })();
    return () => {
      cancelled = true;
    };
  }, [agency]);
  return info;
}

// Resolve coordinates for weather widgets: board config first, then agency
// info. Always numbers (or null), whatever type the settings stored.
export function useCoordinates(agency, board) {
  const info = useAgencyInfo(agency);
  const cfg = board?.dataSources?.coordinates;
  const own = coordPair([cfg?.latitude, cfg?.lat], [cfg?.longitude, cfg?.lng]);
  const c = own || coordPair([info.lat], [info.lng]);
  return { lat: c ? c.lat : null, lng: c ? c.lng : null };
}

// Clock time as the board shows it: 24-hour unless a widget is set to
// "12h" (config.timeFormat). hourCycle h23 (not hour12:false, which some
// engines render as "24:05" after midnight). `tz` is an optional IANA zone.
export function clockTime(date, { timeFormat, tz } = {}) {
  const opts = { hour: "2-digit", minute: "2-digit", hourCycle: timeFormat === "12h" ? "h12" : "h23" };
  if (tz) opts.timeZone = tz;
  return date.toLocaleTimeString([], opts);
}
